import React, { useState, useEffect } from 'react';
import {
	Box,
	Button,
	Select,
	MenuItem,
	TextField,
	Typography,
	useMediaQuery,
	Theme,
} from '@mui/material';
import OTPInput from 'react-otp-input';
import { useNavigate } from 'react-router-dom';
import { countryCodesdata } from '../../../components/common/CountryData';
import { AuthenticationAPI } from '../../../services/api/Vendorpage/Authentication';
import MyHeadings from '../../../components/page/Vendor/Vendor-ui/Headings';
import MySnackbar from '../../../components/page/Vendor/Vendor-ui/Snackbar';

const countryCodes = countryCodesdata;

type SnackState = {
	open: boolean;
	message: string;
	severity: 'success' | 'error' | 'info' | 'warning';
	vertical: 'top' | 'bottom';
	horizontal: 'left' | 'center' | 'right';
};

interface VendoRFormsProps {
	slug?: string;
}

const VendoRForms: React.FC<VendoRFormsProps> = ({ slug }) => {
	const [formValues, setFormValues] = useState({ countryCode: '91', mobile: '', otp: '' });
	const [formErrors, setFormErrors] = useState({ mobile: '', otp: '' });
	const [loading, setLoading] = useState(false);
	const [otpSent, setOtpSent] = useState(false);
	const [resendTimer, setResendTimer] = useState(0);
	const [canResend, setCanResend] = useState(true);
	const Navigate = useNavigate();
	const [snack, setSnack] = useState<SnackState>({
		open: false,
		vertical: 'top',
		horizontal: 'center',
		message: '',
		severity: 'success',
	});

	const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const currentCountry = countryCodes.find((c: any) => c.phone === formValues.countryCode);

	useEffect(() => {
		if (resendTimer > 0) {
			const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
			return () => clearTimeout(timer);
		} else if (resendTimer === 0 && otpSent) setCanResend(true);
	}, [resendTimer, otpSent]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let value = e.target.value;
		if (e.target.name === 'mobile') value = value.replace(/\D/g, '').slice(0, 10);
		setFormValues({ ...formValues, [e.target.name]: value });
		if (formErrors.mobile) setFormErrors({ ...formErrors, mobile: '' });
	};

	const handleCountryCodeChange = (e: any) =>
		setFormValues({ ...formValues, countryCode: e.target.value });

	const handleOtpChange = (otp: string) => {
		setFormValues({ ...formValues, otp });
		if (formErrors.otp) setFormErrors({ ...formErrors, otp: '' });
	};

	const validateMobile = () => {
		const errors: any = {};
		const pattern = currentCountry?.pattern || /^[0-9]{10}$/;
		if (!formValues.mobile) errors.mobile = 'Phone number is required';
		else if (!pattern.test(formValues.mobile))
			errors.mobile = `Phone number must be ${currentCountry?.maxLength || 10} digits (e.g. ${currentCountry?.example || '1234567890'})`;
		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const sendOtp = async (isResend = false) => {
		if (!validateMobile()) return;
		setLoading(true);
		try {
			const payload: any = { mobile: `+${formValues.countryCode} ${formValues.mobile}` };
			const res = isResend
				? await AuthenticationAPI.ResendOtp(payload)
				: await AuthenticationAPI.SendOtp(payload, slug);

			console.log(res, 'res>>>>>');
			if (!res?.data?.success) {
				// setSnack({
				// 	open: true,
				// 	message: res?.error?.errors || 'Failed to send OTP',
				// 	severity: 'error',
				// 	vertical: 'top',
				// 	horizontal: 'center',
				// });
			} else {
				setOtpSent(true);
				setResendTimer(60);
				setCanResend(false);
				// setSnack({
				// 	open: true,
				// 	message: 'OTP sent successfully',
				// 	severity: 'success',
				// 	vertical: 'top',
				// 	horizontal: 'center',
				// });
			}
		} catch (err: any) {
			setSnack({
				open: true,
				message: err?.message || 'Failed to send OTP',
				severity: 'error',
				vertical: 'top',
				horizontal: 'center',
			});
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!formValues.otp || formValues.otp.length !== 6) {
			setFormErrors({ ...formErrors, otp: 'Please enter a valid 6-digit OTP' });
			return;
		}

		localStorage.setItem('vendorperson', `+${formValues.countryCode} ${formValues.mobile}`);
		setLoading(true);
		try {
			const payload = {
				mobile: `+${formValues.countryCode} ${formValues.mobile}`,
				otp: Number(formValues.otp),
			};
			const res = await AuthenticationAPI.Login(payload, slug);
			console.log(res, 'res]]]]]');
			if (!res?.data?.success) {
				setSnack({
					open: true,
					message: res?.data?.errors || 'Login failed',
					severity: 'error',
					vertical: 'top',
					horizontal: 'center',
				});
			} else {
				localStorage.setItem('selli-vendor-accessToken', res?.data?.data?.accessToken);
				localStorage.setItem('selli-vendor-refreshToken', res?.data?.data?.refreshToken);
				Navigate(`/${slug}/me`);
				// setSnack({
				// 	open: true,
				// 	message: 'Login successful',
				// 	severity: 'success',
				// 	vertical: 'top',
				// 	horizontal: 'center',
				// });
			}
		} catch {
			setSnack({
				open: true,
				message: 'Login failed',
				severity: 'error',
				vertical: 'top',
				horizontal: 'center',
			});
		} finally {
			setLoading(false);
		}
	};

	const maskPhoneNumber = (phone: string) => {
		if (phone.length <= 5) return phone;
		const firstTwo = phone.substring(0, 2);
		const lastThree = phone.substring(phone.length - 3);
		return `${firstTwo}${'*'.repeat(phone.length - 5)}${lastThree}`;
	};

	const handleCloseSnack = () => setSnack((prev) => ({ ...prev, open: false }));

	return (
		<Box
			sx={{
				minHeight: 'calc(100vh - 32px)',
				backgroundImage: `url('/assets/Vendor login.png')`,
				backgroundSize: 'cover',
				backgroundPosition: 'center',
				display: 'flex',
				justifyContent: { xs: 'center', sm: 'center', md: 'flex-end' }, // Push form to right
				alignItems: 'center',
				p: 2,
			}}
		>
			<Box
				sx={{
					width: '100%',
					maxWidth: 400,
					p: 4,
					borderRadius: 2,
					boxShadow: 3,
					background: 'white',
					mr: { xs: 0, sm: 0, md: 8 }, // Add margin from right edge (only on md+ screens)
				}}
			>
				<Box>
					<Box textAlign="center" mb={3}>
						<img
							src="/assets/login-logo.png"
							alt="Login Logo"
							style={{ width: isSmallScreen ? '80px' : '100px', height: 'auto' }}
						/>
						<MyHeadings
							Title="Vendor Login"
							subtitle="Enter your phone number to continue"
						/>
					</Box>

					<form onSubmit={handleSubmit}>
						{/* Phone Input */}
						{!otpSent && (
							<Box mb={2}>
								<Typography variant="body2">Phone</Typography>
								<Box display="flex" gap={1} mt={1}>
									<Select
										value={formValues.countryCode}
										onChange={handleCountryCodeChange}
										size="small"
										sx={{ width: '100px' }}
									>
										{countryCodes.map((country: any) => (
											<MenuItem key={country.code} value={country.phone}>
												<img
													loading="lazy"
													width="20"
													srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
													src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
													alt=""
												/>
												+{country.phone}
											</MenuItem>
										))}
									</Select>
									<TextField
										name="mobile"
										size="small"
										value={formValues.mobile}
										onChange={handleChange}
										fullWidth
										placeholder="Enter phone"
										error={!!formErrors.mobile}
										helperText={formErrors.mobile}
										inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
									/>
								</Box>
								<Button
									variant="contained"
									fullWidth
									onClick={() => sendOtp()}
									sx={{ mt: 2, background: '#0C8040' }}
									disabled={!formValues.mobile || loading}
								>
									Send OTP
								</Button>
							</Box>
						)}

						{/* OTP Input */}
						{otpSent && (
							<>
								<Typography variant="body2" align="center" mb={1}>
									Enter the 6-digit code sent to +{formValues.countryCode}
									{maskPhoneNumber(formValues.mobile)}
								</Typography>
								<Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
									<OTPInput
										value={formValues.otp}
										onChange={handleOtpChange}
										numInputs={6}
										inputStyle={{
											width: '2rem',
											height: '2rem',
											margin: '0 0.25rem',
											borderRadius: '4px',
											border: '1px solid rgba(0,0,0,0.3)',
										}}
										renderInput={(props) => <input {...props} />}
									/>
								</Box>
								<Button
									type="submit"
									variant="contained"
									fullWidth
									disabled={!formValues.otp || loading}
									sx={{ background: '#0C8040' }}
								>
									Login
								</Button>
								<Box textAlign="center" mt={2}>
									{resendTimer > 0 ? (
										<Typography variant="caption">
											Resend OTP in {resendTimer}s
										</Typography>
									) : (
										<Button
											onClick={() => sendOtp(true)}
											sx={{ background: '#0C8040' }}
											disabled={!canResend}
										>
											Resend OTP
										</Button>
									)}
								</Box>
							</>
						)}
					</form>
				</Box>
			</Box>

			{/* Snackbar */}
			<MySnackbar
				open={snack.open}
				message={snack.message}
				severity={snack.severity}
				position={{ vertical: snack.vertical, horizontal: snack.horizontal }}
				onClose={handleCloseSnack}
			/>
		</Box>
	);
};

export default VendoRForms;
